import { z } from "zod";
import { assignableTeamMemberRoleZod, teamMemberRoleZod } from "./enums.zod.js";
import { isReservedTenantSlug, TENANT_SLUG_REGEX } from "./tenant.utils.js";
import { zInteger, zPhoneNumber, zString, zTimeEpoch, zTimestamps } from "./zod_utils.js";
/**
 * TeamApp Schemas
 */
export const teamAppAutoGeneratedZod = z.object({
    id: z.ulid(),
});
export const teamAppMandatoryZod = z.object({
    name: zString.min(1, "Team name is required"),
});
/** `#rgb` or `#rrggbb`, lower- or upper-case. */
export const zHexColor = zString
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Must be a hex colour like #4f46e5");
/**
 * Subdomain label a team claims for its private PWA (`<slug>.conferhall.com`).
 * DNS-label shaped and never one of the reserved platform labels.
 */
export const zTenantSlug = zString
    .toLowerCase()
    .regex(TENANT_SLUG_REGEX, "3–63 lowercase letters, digits or hyphens; no leading/trailing hyphen")
    .refine((s) => !isReservedTenantSlug(s), { message: "This subdomain is reserved" });
/**
 * Per-team white-label settings. Every field is optional on the row; the
 * frontend falls back to the ConferHall defaults for anything unset.
 */
export const teamAppBrandingZod = z.object({
    slug: zTenantSlug.nullable(),
    appName: zString.min(1).max(60).nullable(),
    appShortName: zString.min(1).max(12).nullable(),
    tagline: zString.max(160).nullable(),
    pwaIconUrl: z.url().max(2048).nullable(),
    primaryColor: zHexColor.nullable(),
    secondaryColor: zHexColor.nullable(),
    backgroundColor: zHexColor.nullable(),
});
export const teamAppOptionalZod = z.object({
    logoUrl: z.url().max(2048).nullable(),
    createdByUserId: z.uuid(),
    personalTeamForUserId: z.uuid().nullable(),
    deletedAt: zTimeEpoch.nullable(),
}).extend(teamAppBrandingZod.shape);
export const teamAppCreateInputZod = teamAppMandatoryZod.extend(teamAppOptionalZod.partial().shape);
// API-facing create schema. Excludes server-controlled identity/tenancy fields
// (`createdByUserId`, `personalTeamForUserId`, `deletedAt`) so authenticated
// clients can't spoof team ownership or burn another user's personal-team slot
// (soft-delete-partial unique on `personal_team_for_user_id`). The server
// derives `createdByUserId` from the session and never accepts
// `personalTeamForUserId` from clients — it's set only by server-only paths
// (`getDefaultTeam` / `UserTable.afterCreate`).
export const teamAppCreateApiInputZod = teamAppMandatoryZod.extend(teamAppOptionalZod.pick({ logoUrl: true }).partial().shape);
// Owner/Admin-editable fields: identity + white-label branding. Tenancy
// fields stay server-owned exactly as on create, and so does the subdomain:
// ConferHall assigns it from the super-admin console (DNS and the
// certificate are theirs to arrange), the team only reads it.
export const teamAppUpdateApiInputZod = teamAppMandatoryZod
    .extend(teamAppOptionalZod.pick({ logoUrl: true }).shape)
    .extend(teamAppBrandingZod.omit({ slug: true }).shape)
    .partial();
/** Super-admin: assign (or clear) a team's subdomain. */
export const teamSlugAssignInputZod = z.object({
    teamId: z.ulid(),
    slug: zTenantSlug.nullable(),
});
/** Super-admin: how many rooms a team may run at once (0 turns rooms off). */
export const TEAM_ROOM_CAPACITY_MAX = 5000;
export const teamRoomCapacityAssignInputZod = z.object({
    teamId: z.ulid(),
    roomCapacity: zInteger(0, TEAM_ROOM_CAPACITY_MAX),
});
export const teamAppSelectAllZod = teamAppMandatoryZod
    .extend(teamAppOptionalZod.shape)
    .extend(teamAppAutoGeneratedZod.shape)
    .extend({
    /**
     * Rooms the team may run at once at its stands (root ADR-036); 0 = rooms
     * off. Assigned by the super-admin with the minutes, never by the team.
     */
    roomCapacity: zInteger(0),
})
    .extend(zTimestamps);
/**
 * Public, unauthenticated projection of a team's branding, keyed by slug.
 * Served to `<slug>.<root>` before login so the login page, manifest and
 * theme can be branded. Deliberately excludes membership/ownership fields.
 */
export const teamBrandingPublicZod = teamAppBrandingZod
    .extend({
    teamId: z.ulid(),
    name: teamAppMandatoryZod.shape.name,
    logoUrl: teamAppOptionalZod.shape.logoUrl,
})
    .extend({ slug: zTenantSlug });
export const teamBrandingLookupInputZod = z.object({ slug: zTenantSlug });
/**
 * TeamMember Schemas
 */
export const teamAppMemberAutoGeneratedZod = z.object({
    id: z.ulid(),
});
export const teamAppMemberMandatoryZod = z.object({
    teamId: z.ulid(),
    role: teamMemberRoleZod,
    addedAt: zTimeEpoch,
});
export const teamAppMemberOptionalZod = z.object({
    userId: z.uuid().nullable(),
    email: z.email().max(320).nullable(),
    phoneNumber: zPhoneNumber.nullable(),
    joinedAt: zTimeEpoch.nullable(),
    deletedAt: zTimeEpoch.nullable(),
});
export const teamAppMemberSelectAllZod = teamAppMemberMandatoryZod
    .extend(teamAppMemberOptionalZod.shape)
    .extend(teamAppMemberAutoGeneratedZod.shape)
    .extend(zTimestamps);
export const teamAppMemberRoleZod = assignableTeamMemberRoleZod;
// `teamId` is derived from the `x-team-id` header on the RPC procedure, not
// from the request body — callers implicitly add members to their active team.
/** A password a person can actually type and that better-auth will take. */
export const passwordZod = z.string().min(8, "At least 8 characters").max(128);
export const teamAppMemberAddInputZod = z.object({
    email: z.email().max(320).optional().nullable(),
    phoneNumber: zPhoneNumber.optional().nullable(),
    role: assignableTeamMemberRoleZod,
    /**
     * For a colleague with no account yet: the organiser creates it here, with
     * a password they hand over, so the member signs in with email + password
     * without an invitation email. Ignored when the email already has an
     * account — that person keeps their own password.
     */
    name: z.string().trim().min(1).max(120).optional(),
    password: passwordZod.optional(),
}).refine(data => data.email || data.phoneNumber, {
    message: "Either email or phone number must be provided",
    path: ["email"],
}).refine(data => !data.password || data.name, {
    message: "A name is needed to create the account",
    path: ["name"],
});
/** Owner/Admin resets a member's password; the member's other sessions end. */
export const teamMemberSetPasswordInputZod = z.object({
    id: z.ulid(),
    password: passwordZod,
});
export const teamWithRoleZod = teamAppSelectAllZod.extend({
    joinedAt: zTimeEpoch.nullable(),
    userRole: teamMemberRoleZod,
});
//# sourceMappingURL=team_app.zod.js.map